import React, { useState, useEffect } from 'react';
import {
  VideoCameraIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { staffApi } from '../staffApi';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
};

export default function StaffRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await staffApi.getRecordings();
      setRecordings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setError(err.message || 'Failed to load recordings');
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const handleDownload = async (bookingId, s3Key, fileName) => {
    const key = `${bookingId}-${s3Key}`;
    try {
      setDownloading(key);
      const url = await staffApi.getRecordingDownloadUrl(bookingId, s3Key);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to get download link');
    } finally {
      setDownloading(null);
    }
  };

  const handleCopyLink = async (bookingId, s3Key) => {
    try {
      const url = await staffApi.getRecordingDownloadUrl(bookingId, s3Key);
      await navigator.clipboard.writeText(url);
      setCopySuccess(`${bookingId}-${s3Key}`);
      setTimeout(() => setCopySuccess(null), 2500);
    } catch (err) {
      console.error('Copy error:', err);
      setError(err.message || 'Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
              Meeting Recordings
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Download recordings from completed tutoring sessions
            </p>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
          <p className="mt-4 text-gray-600">Loading recordings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
            Meeting Recordings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Download recordings from completed tutoring sessions
          </p>
        </div>
        <button
          onClick={fetchRecordings}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {recordings.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="text-center">
            <VideoCameraIcon className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No recordings yet</h3>
            <p className="mt-2 text-sm text-gray-500">
              Completed meetings will appear here once recordings are available.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {recordings.map((item) => (
              <li key={item.booking_id} className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatDate(item.date)} · {formatTime(item.start_time)} – {formatTime(item.end_time)}
                    </p>
                    <p className="text-sm text-gray-500">
                      Tutor: {item.tutor_name} · Student: {item.student_name}
                    </p>
                  </div>
                </div>
                {item.recordings.length === 0 ? (
                  <p className="mt-3 text-sm text-amber-600">
                    Recording not available yet. It may still be processing. Try refreshing later.
                  </p>
                ) : (
                  <div className="mt-4 space-y-2">
                    {item.recordings.map((rec) => (
                      <div
                        key={rec.s3_key}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md"
                      >
                        <span className="text-sm font-medium text-gray-700 truncate flex-1 mr-4">
                          {rec.file_name}
                        </span>
                        <span className="text-xs text-gray-500 mr-4 shrink-0">
                          {formatFileSize(rec.size)}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleDownload(item.booking_id, rec.s3_key, rec.file_name)}
                            disabled={downloading === `${item.booking_id}-${rec.s3_key}`}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            Download
                          </button>
                          <button
                            onClick={() => handleCopyLink(item.booking_id, rec.s3_key)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            title="Copy link to paste in another tab if download doesn't start"
                          >
                            <LinkIcon className="h-4 w-4 mr-1" />
                            {copySuccess === `${item.booking_id}-${rec.s3_key}` ? 'Copied!' : 'Copy link'}
                          </button>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500 mt-2">
                      If the download doesn't start automatically (e.g. for large files), use "Copy link" and paste it in a new tab.
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
